<template>
  <aside
    class="appointment-report ar-card nap-summary"
    :aria-labelledby="titleId"
  >
    <h2
      :id="titleId"
      class="nap-summary__title"
    >
      {{ NEW_COPY.appointmentSummaryTitle }}
    </h2>

    <dl class="nap-summary__grid">
      <div
        v-for="item in items"
        :key="item.key"
        class="nap-summary__item"
      >
        <dt class="nap-summary__label">
          {{ item.label }}
        </dt>
        <!-- `key` muda com o valor: o valor novo entra com 2px de subida (180ms). -->
        <dd
          :key="`${item.key}:${item.chip || item.text || ''}`"
          class="nap-summary__value ar-tabular"
          :class="{ 'nap-anim-value': item.chip || item.text }"
        >
          <span
            v-if="item.initials"
            class="ar-avatar ar-avatar--sm"
            aria-hidden="true"
          >{{ item.initials }}</span>
          <span
            v-if="item.chip"
            class="ar-chip"
            :class="`ar-chip--${item.tone || 'neutral'}`"
          >{{ item.chip }}</span>
          <span
            v-else
            class="nap-summary__text"
            :class="{
              'nap-summary__text--empty': !item.text,
              'nap-summary__text--offset': !!item.initials,
            }"
          >{{ item.text || EMPTY_VALUE }}</span>
          <feather-icon
            v-if="item.warn"
            icon="AlertCircleIcon"
            size="14"
            class="nap-summary__warn"
            aria-hidden="true"
          />
        </dd>
      </div>
    </dl>

    <div
      class="nap-summary__divider"
      aria-hidden="true"
    />

    <div class="nap-summary__format">
      <span class="nap-summary__label">{{ formatLabel }}</span>
      <div
        v-if="format"
        :key="`${format.kind}:${format.detail}`"
        class="nap-summary__format-value nap-anim-value"
      >
        <feather-icon
          :icon="format.icon"
          size="16"
          class="nap-summary__format-icon"
          aria-hidden="true"
        />
        <span class="nap-summary__format-text">
          <span class="nap-summary__format-name">{{ format.label }}</span>
          <span
            v-if="format.detail"
            class="nap-summary__format-detail"
          >{{ format.detail }}</span>
        </span>
      </div>
      <span
        v-else
        class="nap-summary__text nap-summary__text--empty"
      >{{ EMPTY_VALUE }}</span>
    </div>

    <p
      class="ar-callout ar-callout--info nap-summary__note"
    >
      <feather-icon
        icon="ClockIcon"
        size="16"
        aria-hidden="true"
      />
      <span
        :key="note"
        class="ar-tabular nap-anim-value"
      >{{ note }}</span>
    </p>
  </aside>
</template>

<script>
import { NEW_COPY } from '@/constants/appointment_report_v2'
import { nextReportUid } from '../../helpers/report_ui'

// Valor ainda não escolhido (board: travessão muted).
const EMPTY_VALUE = '—'

/**
 * Rail "Resumo do compromisso" (board v4-novo-compromisso): prévia ao vivo dos valores escolhidos, no mesmo
 * layout de campos do cartão "Compromisso" do report, + formato de reunião + quando o report é liberado.
 *
 * `items`: `[{ key, label, text?, initials?, chip?, tone?, warn? }]` (tone = sufixo de `.ar-chip--*`).
 * `format`: `{ kind, label, detail, icon }` ou `null` (sem formato escolhido).
 */
export default {
  name: 'NewAppointmentSummary',
  props: {
    items: {
      type: Array,
      required: true,
    },
    formatLabel: {
      type: String,
      required: true,
    },
    format: {
      type: Object,
      default: null,
    },
    note: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      NEW_COPY,
      EMPTY_VALUE,
      titleId: nextReportUid('nap-summary'),
    }
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>
<style lang="scss" src="./_new-appointment.scss"></style>

<style lang="scss" scoped>
.nap-summary {
  padding: 20px;
}

.nap-summary__title {
  margin: 0 0 16px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.nap-summary__grid {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 12px;
  row-gap: 16px;
}

.nap-summary__item {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.nap-summary__label {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: var(--ar-text-label);
}

.nap-summary__value {
  min-width: 0;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.nap-summary__text {
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
  overflow-wrap: anywhere;
}

.nap-summary__text--offset {
  padding-top: 2px;
}

.nap-summary__text--empty {
  color: var(--ar-text-muted);
}

.nap-summary__warn {
  flex-shrink: 0;
  margin-top: 3px;
  color: var(--ar-danger);
}

.nap-summary__divider {
  height: 1px;
  margin: 16px 0;
  background: var(--ar-divider);
}

.nap-summary__format {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.nap-summary__format-value {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.nap-summary__format-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--ar-brand-text);
}

.nap-summary__format-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.nap-summary__format-name {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
}

.nap-summary__format-detail {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);
  overflow-wrap: anywhere;
}

.nap-summary__note {
  margin-top: 20px;
  font-weight: 400;
}
</style>
